import { Watchable } from '@cotera/client/v0/actions/framework';
import { getDataGridVisibleColumnOrder } from './column-layout';
import type { ReadonlyWatchable } from '@cotera/client/v0/actions/framework';
import type {
  DataGridCellAddress,
  DataGridCellEdit,
  DataGridCellRange,
  DataGridCellValue,
  DataGridColumn,
  DataGridColumnDisplayOptions,
  DataGridFilter,
  DataGridPinnedSide,
  DataGridRowHeight,
  DataGridRowId,
  DataGridSort,
  DataGridSortDirection,
} from './types';
import {
  DATA_GRID_ROW_HEIGHTS,
  DEFAULT_DATA_GRID_DISPLAY_OPTIONS,
  DEFAULT_DATA_GRID_ROW_HEIGHT,
  dataGridEditKey,
} from './types';

export type DataGridFocusedCell = DataGridCellAddress;

export type DataGridViewModel<TRow> = {
  columns: Watchable<DataGridColumn<TRow>[]>;
  focusedCell: Watchable<DataGridFocusedCell | null>;
  selectedRowIds: Watchable<Set<DataGridRowId>>;
  selectedCellRanges: Watchable<DataGridCellRange[]>;
  cellSelectionAnchor: Watchable<DataGridCellAddress | null>;
  selectionAnchor: Watchable<DataGridRowId | null>;
  sorts: Watchable<DataGridSort[]>;
  filters: Watchable<DataGridFilter[]>;
  headersExpanded: Watchable<boolean>;
  columnDisplayOptions: Watchable<
    Record<string, DataGridColumnDisplayOptions | undefined>
  >;
  editingCell: Watchable<DataGridCellAddress | null>;
  /**
   * The character an edit was started by typing, for the editor to open on.
   * Null for every other way in, and cleared as soon as the edit settles.
   */
  editSeed: ReadonlyWatchable<string | null>;
  pendingEdits: Watchable<Record<string, DataGridCellEdit | undefined>>;
  scrollX: Watchable<number>;
  scrollY: Watchable<number>;
  /**
   * Bumped to ask the body to go back to the top.
   *
   * A counter rather than a position, and separate from {@link scrollY}, which
   * only ever reports where the reader has scrolled *to*. Writing a position
   * into that would race the scroll handler writing it back and read as the
   * grid fighting the mouse; a request is unambiguous and cannot be mistaken
   * for a report.
   */
  rewindRequests: ReadonlyWatchable<number>;
  /**
   * Rows showing their detail panel. Kept by row id rather than index so it
   * survives paging and re-sorting, which renumber every row.
   */
  expandedRowIds: Watchable<Set<DataGridRowId>>;
  /**
   * The named size the user picked. The pixel count below follows from it, so
   * a host persisting the preference stores this rather than a height.
   */
  rowHeightPreset: Watchable<DataGridRowHeight>;
  /** What the preset works out to, which is what the grid lays rows out from. */
  rowHeight: ReadonlyWatchable<number>;
  /**
   * Extra height an expanded row takes. Fixed rather than measured: the grid
   * positions every row from this number, so a panel that resized itself after
   * paint would drag everything below it.
   */
  expansionHeight: ReadonlyWatchable<number>;
  headerHeight: ReadonlyWatchable<number>;
  totalRows: ReadonlyWatchable<number | null>;
  totalLoadedRows: ReadonlyWatchable<number>;

  focusCell(address: DataGridFocusedCell): void;
  /**
   * Focuses the first cell of a row the user can type into, if it has one.
   *
   * Where "+ Row" leaves the user: the row is appended out of sight at the
   * bottom of a long grid, and without this the button reads as having done
   * nothing at all.
   */
  focusFirstEditableCell(rowId: DataGridRowId, rowIndex: number): boolean;
  clearFocus(): void;
  selectOnly(rowId: DataGridRowId): void;
  /**
   * What an unmodified click on a row means: pick this row alone, or drop the
   * selection when it is already the only one picked. The second click has to
   * undo the first — without it a plain click is a one-way door, and the only
   * way back out is a modified click the user has no reason to guess at.
   */
  toggleSelectOnly(rowId: DataGridRowId): void;
  clearSelection(): void;
  toggleRowSelection(rowId: DataGridRowId): void;
  selectRowRange(
    anchor: DataGridRowId,
    target: DataGridRowId,
    visibleRows: TRow[],
    getRowId: (row: TRow) => DataGridRowId
  ): void;
  toggleRowExpanded(rowId: DataGridRowId): void;
  collapseAllRows(): void;
  selectCell(address: DataGridCellAddress): void;
  /**
   * The cell counterpart of {@link toggleSelectOnly}: pick this cell alone, or
   * clear when it is already the whole selection. Clearing drops focus too,
   * since focus is drawn as a ring and a cell that kept one would not look
   * cleared.
   */
  toggleSelectCellOnly(address: DataGridCellAddress): void;
  toggleCell(address: DataGridCellAddress): void;
  selectCellRange(
    anchor: DataGridCellAddress,
    target: DataGridCellAddress
  ): void;
  clearCellSelection(): void;
  setSort(columnId: string, direction: DataGridSortDirection | null): void;
  toggleSort(columnId: string, direction: DataGridSortDirection): void;
  setFilter(columnId: string, value: unknown): void;
  clearFilter(columnId: string): void;
  clearFilters(): void;
  clearSorts(): void;
  setColumnDisplayOptions(
    columnId: string,
    options: Partial<DataGridColumnDisplayOptions>
  ): void;
  setColumnVisible(columnId: string, visible: boolean): void;
  /**
   * Asks the body to go back to the top, for a re-read that replaced the
   * population rather than refreshing it.
   *
   * Left to itself the browser clamps a scroll offset that no longer exists —
   * filtering a hundred thousand rows down to fifteen while scrolled halfway
   * gives back a viewport at some arbitrary place in the new result, arrived at
   * by nobody's decision and different for every keystroke. One deliberate move
   * to a known place reads as an answer; the clamp reads as the table lurching.
   */
  rewind(): void;
  setRowHeight(rowHeight: DataGridRowHeight): void;
  beginEdit(address: DataGridCellAddress, seed?: string): void;
  cancelEdit(): void;
  /**
   * Records a committed edit. Returns the edit, or null when the value went
   * back to the originally loaded one — that case clears the dirty flag.
   */
  commitEdit(
    address: DataGridCellAddress,
    value: DataGridCellValue,
    loadedValue: unknown
  ): DataGridCellEdit | null;
  clearEdits(): void;
  resizeColumn(columnId: string, width: number): void;
  reorderColumn(columnId: string, targetColumnId: string): void;
  pinColumn(columnId: string, side: DataGridPinnedSide): void;
  unpinColumn(columnId: string): void;
  setTotalRows(totalRows: number | null): void;
  setTotalLoadedRows(totalLoadedRows: number): void;
};

type CreateDataGridViewModelOptions<TRow> = {
  columns: DataGridColumn<TRow>[];
  /**
   * Pixel height of a `standard` row, for grids whose rows are not the usual
   * size. The other two presets keep their own heights.
   */
  rowHeight?: number;
  /** Which size the grid opens on, typically a restored preference. */
  rowHeightPreset?: DataGridRowHeight;
  /** Height of a row's detail panel. 0 disables expansion entirely. */
  expansionHeight?: number;
  headerHeight?: number;
  expandedHeaderHeight?: number;
  totalRows?: number | null;
  totalLoadedRows?: number;
};

const DEFAULT_ROW_HEIGHT = 36;
const DEFAULT_HEADER_HEIGHT = 40;
// Tall enough for a stats caption, three category rows and a "+ N more" row.
const DEFAULT_EXPANDED_HEADER_HEIGHT = 124;

const cloneSet = <T>(value: Set<T>): Set<T> => new Set(value);

const sameCell = (
  left: DataGridCellAddress,
  right: DataGridCellAddress
): boolean => left.rowId === right.rowId && left.columnId === right.columnId;

const clampColumnWidth = <TRow>(
  column: DataGridColumn<TRow>,
  width: number
): number => {
  const minWidth = column.minWidth ?? 80;
  const maxWidth = column.maxWidth ?? 600;
  return Math.min(maxWidth, Math.max(minWidth, width));
};

export function createDataGridViewModel<TRow>({
  columns,
  rowHeight = DEFAULT_ROW_HEIGHT,
  rowHeightPreset = DEFAULT_DATA_GRID_ROW_HEIGHT,
  expansionHeight = 0,
  headerHeight = DEFAULT_HEADER_HEIGHT,
  expandedHeaderHeight = DEFAULT_EXPANDED_HEADER_HEIGHT,
  totalRows = null,
  totalLoadedRows = 0,
}: CreateDataGridViewModelOptions<TRow>): DataGridViewModel<TRow> {
  const columnsWatchable = Watchable.fromValue(columns);
  const focusedCell = Watchable.fromValue<DataGridFocusedCell | null>(null);
  const selectedRowIds = Watchable.fromValue<Set<DataGridRowId>>(new Set(), {
    updater: cloneSet,
  });
  const selectedCellRanges = Watchable.fromValue<DataGridCellRange[]>([]);
  const cellSelectionAnchor = Watchable.fromValue<DataGridCellAddress | null>(
    null
  );
  const selectionAnchor = Watchable.fromValue<DataGridRowId | null>(null);
  const sorts = Watchable.fromValue<DataGridSort[]>([]);
  const filters = Watchable.fromValue<DataGridFilter[]>([]);
  const headersExpanded = Watchable.fromValue(false);
  const columnDisplayOptions = Watchable.fromValue<
    Record<string, DataGridColumnDisplayOptions | undefined>
  >({});
  const editingCell = Watchable.fromValue<DataGridCellAddress | null>(null);
  const editSeed = Watchable.fromValue<string | null>(null);
  const pendingEdits = Watchable.fromValue<
    Record<string, DataGridCellEdit | undefined>
  >({});
  const scrollX = Watchable.fromValue(0);
  const scrollY = Watchable.fromValue(0);
  const rewindRequests = Watchable.fromValue(0);
  const expandedRowIds = Watchable.fromValue<Set<DataGridRowId>>(new Set(), {
    updater: cloneSet,
  });
  const rowHeightPresetWatchable =
    Watchable.fromValue<DataGridRowHeight>(rowHeightPreset);
  // `standard` defers to whatever height the host asked for, so a grid that
  // sizes its own rows keeps that size until the user picks another preset.
  const rowHeightWatchable = Watchable.from((get) => {
    const preset = get(rowHeightPresetWatchable);
    return preset === 'standard' ? rowHeight : DATA_GRID_ROW_HEIGHTS[preset];
  });
  const expansionHeightWatchable = Watchable.fromValue(expansionHeight);
  const headerHeightWatchable = Watchable.from((get) =>
    get(headersExpanded) ? expandedHeaderHeight : headerHeight
  );
  const totalRowsWatchable = Watchable.fromValue<number | null>(totalRows);
  const totalLoadedRowsWatchable = Watchable.fromValue(totalLoadedRows);

  return {
    columns: columnsWatchable,
    focusedCell,
    selectedRowIds,
    selectedCellRanges,
    cellSelectionAnchor,
    selectionAnchor,
    sorts,
    filters,
    headersExpanded,
    columnDisplayOptions,
    editingCell,
    editSeed,
    pendingEdits,
    scrollX,
    scrollY,
    rewindRequests,
    expandedRowIds,
    rowHeightPreset: rowHeightPresetWatchable,
    rowHeight: rowHeightWatchable,
    expansionHeight: expansionHeightWatchable,
    headerHeight: headerHeightWatchable,
    totalRows: totalRowsWatchable,
    totalLoadedRows: totalLoadedRowsWatchable,

    focusCell(address) {
      focusedCell.set(address);
    },

    rewind() {
      rewindRequests.set(rewindRequests.snapshot() + 1);
    },

    focusFirstEditableCell(rowId, rowIndex) {
      // Through the layout's own ordering: a cell address counts columns as the
      // grid draws them, pinned groups first, not as they sit in this array.
      const ordered = getDataGridVisibleColumnOrder(
        columnsWatchable.snapshot()
      );
      const columnIndex = ordered.findIndex(
        (column) => column.computed !== true && column.editable === true
      );
      const column = ordered[columnIndex];
      if (column === undefined) {
        return false;
      }
      focusedCell.set({ rowId, rowIndex, columnId: column.id, columnIndex });
      return true;
    },

    clearFocus() {
      focusedCell.set(null);
    },

    selectOnly(rowId) {
      selectedRowIds.set(new Set([rowId]));
      selectionAnchor.set(rowId);
    },

    toggleSelectOnly(rowId) {
      const current = selectedRowIds.snapshot();
      // Only when it is the whole selection. Plain-clicking one of several
      // picked rows narrows to it, which is the usual meaning and not an
      // accidental clear of the other rows.
      if (current.size === 1 && current.has(rowId)) {
        selectedRowIds.set(new Set());
        selectionAnchor.set(null);
        return;
      }
      selectedRowIds.set(new Set([rowId]));
      selectionAnchor.set(rowId);
    },

    clearSelection() {
      selectedRowIds.set(new Set());
      selectionAnchor.set(null);
    },

    toggleRowSelection(rowId) {
      const next = new Set(selectedRowIds.snapshot());
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      selectedRowIds.set(next);
      selectionAnchor.set(rowId);
    },

    selectRowRange(anchor, target, visibleRows, getRowId) {
      const ids = visibleRows.map((row) => getRowId(row));
      const anchorIndex = ids.indexOf(anchor);
      const targetIndex = ids.indexOf(target);
      if (anchorIndex === -1 || targetIndex === -1) {
        selectedRowIds.set(new Set([target]));
        selectionAnchor.set(target);
        return;
      }

      const start = Math.min(anchorIndex, targetIndex);
      const end = Math.max(anchorIndex, targetIndex);
      selectedRowIds.set(new Set(ids.slice(start, end + 1)));
      selectionAnchor.set(anchor);
    },

    toggleRowExpanded(rowId) {
      const next = new Set(expandedRowIds.snapshot());
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      expandedRowIds.set(next);
    },

    collapseAllRows() {
      expandedRowIds.set(new Set());
    },

    selectCell(address) {
      focusedCell.set(address);
      selectedCellRanges.set([{ start: address, end: address }]);
      cellSelectionAnchor.set(address);
    },

    toggleSelectCellOnly(address) {
      const current = selectedCellRanges.snapshot();
      const first = current[0];
      const alreadyOnlyThis =
        current.length === 1 &&
        first !== undefined &&
        sameCell(first.start, address) &&
        sameCell(first.end, address);
      if (alreadyOnlyThis) {
        // Focus goes with the selection. It is drawn as a ring on the cell, so
        // leaving it behind means the click that was meant to clear the cell
        // visibly does not — the tint goes and a border stays, which reads as
        // the grid ignoring the click rather than as two separate states.
        selectedCellRanges.set([]);
        cellSelectionAnchor.set(null);
        focusedCell.set(null);
        return;
      }
      focusedCell.set(address);
      selectedCellRanges.set([{ start: address, end: address }]);
      cellSelectionAnchor.set(address);
    },

    toggleCell(address) {
      const current = selectedCellRanges.snapshot();
      const withoutAddress = current.filter(
        (range) =>
          !sameCell(range.start, address) || !sameCell(range.end, address)
      );
      selectedCellRanges.set(
        withoutAddress.length === current.length
          ? [...current, { start: address, end: address }]
          : withoutAddress
      );
      focusedCell.set(address);
      cellSelectionAnchor.set(address);
    },

    selectCellRange(anchor, target) {
      selectedCellRanges.set([{ start: anchor, end: target }]);
      focusedCell.set(target);
      cellSelectionAnchor.set(anchor);
    },

    clearCellSelection() {
      selectedCellRanges.set([]);
      cellSelectionAnchor.set(null);
    },

    setSort(columnId, direction) {
      sorts.set(
        direction === null
          ? sorts.snapshot().filter((sort) => sort.columnId !== columnId)
          : [
              ...sorts.snapshot().filter((sort) => sort.columnId !== columnId),
              { columnId, direction },
            ]
      );
    },

    toggleSort(columnId, direction) {
      const existing = sorts
        .snapshot()
        .find((sort) => sort.columnId === columnId);
      sorts.set(
        existing?.direction === direction
          ? sorts.snapshot().filter((sort) => sort.columnId !== columnId)
          : [
              ...sorts.snapshot().filter((sort) => sort.columnId !== columnId),
              { columnId, direction },
            ]
      );
    },

    setFilter(columnId, value) {
      filters.set([
        ...filters.snapshot().filter((filter) => filter.columnId !== columnId),
        { columnId, value },
      ]);
    },

    clearFilter(columnId) {
      filters.set(
        filters.snapshot().filter((filter) => filter.columnId !== columnId)
      );
    },

    clearFilters() {
      filters.set([]);
    },

    clearSorts() {
      sorts.set([]);
    },

    setColumnDisplayOptions(columnId, options) {
      const current = columnDisplayOptions.snapshot();
      columnDisplayOptions.set({
        ...current,
        [columnId]: {
          ...DEFAULT_DATA_GRID_DISPLAY_OPTIONS,
          ...current[columnId],
          ...options,
        },
      });
    },

    setColumnVisible(columnId, visible) {
      columnsWatchable.set(
        columnsWatchable
          .snapshot()
          .map((column) =>
            column.id === columnId ? { ...column, visible } : column
          )
      );
    },

    setRowHeight(next) {
      rowHeightPresetWatchable.set(next);
    },

    beginEdit(address, seed) {
      editSeed.set(seed ?? null);
      editingCell.set(address);
    },

    cancelEdit() {
      editSeed.set(null);
      editingCell.set(null);
    },

    commitEdit(address, value, loadedValue) {
      editSeed.set(null);
      editingCell.set(null);
      const key = dataGridEditKey(address.rowId, address.columnId);
      const current = pendingEdits.snapshot();
      // The baseline is whatever the row held before the first edit, so a
      // round trip back to it removes the entry rather than stacking one.
      const previousValue = current[key]?.previousValue ?? loadedValue;
      const next = { ...current };
      if (Object.is(value, previousValue)) {
        delete next[key];
        pendingEdits.set(next);
        return null;
      }
      const edit: DataGridCellEdit = {
        rowId: address.rowId,
        columnId: address.columnId,
        previousValue,
        value,
      };
      next[key] = edit;
      pendingEdits.set(next);
      return edit;
    },

    clearEdits() {
      pendingEdits.set({});
      editingCell.set(null);
    },

    resizeColumn(columnId, width) {
      columnsWatchable.set(
        columnsWatchable
          .snapshot()
          .map((column) =>
            column.id === columnId
              ? { ...column, width: clampColumnWidth(column, width) }
              : column
          )
      );
    },

    reorderColumn(columnId, targetColumnId) {
      const next = [...columnsWatchable.snapshot()];
      const fromIndex = next.findIndex((column) => column.id === columnId);
      const toIndex = next.findIndex((column) => column.id === targetColumnId);
      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
        return;
      }

      const [column] = next.splice(fromIndex, 1);
      if (column === undefined) {
        return;
      }
      next.splice(toIndex, 0, column);
      columnsWatchable.set(next);
    },

    pinColumn(columnId, side) {
      columnsWatchable.set(
        columnsWatchable
          .snapshot()
          .map((column) =>
            column.id === columnId ? { ...column, pinned: side } : column
          )
      );
    },

    unpinColumn(columnId) {
      columnsWatchable.set(
        columnsWatchable
          .snapshot()
          .map((column) =>
            column.id === columnId ? { ...column, pinned: null } : column
          )
      );
    },

    setTotalRows(value) {
      totalRowsWatchable.set(value);
    },

    setTotalLoadedRows(value) {
      totalLoadedRowsWatchable.set(value);
    },
  };
}
